import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { Feature } from 'ol';
import { Point } from 'ol/geom';
import { fromLonLat, transform } from 'ol/proj';
import { Fill, Stroke, Style } from 'ol/style';
import CircleStyle from 'ol/style/Circle';
import axiosInstance from '../axiosIntance';

interface BiensOffMarketLayerOptions {
  visible: boolean;
  selectedParcelle?: string | null;
}

// Styles based on contract type and status
const biensOffMarketStyles = {
  // Vente -> Noir (Black)
  vente: {
    default: new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "black" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
    }),
    selected: [
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: "black" }),
          stroke: new Stroke({
            color: "white",
            width: 2,
          }),
        }),
        zIndex: 1,
      }),
      new Style({
        image: new CircleStyle({
          radius: 9,
          fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
        }),
      }),
    ],
  },
  // Location -> Orange (current color)
  location: {
    default: new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "orange" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
    }),
    selected: [
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: "orange" }),
          stroke: new Stroke({
            color: "white",
            width: 2,
          }),
        }),
        zIndex: 1,
      }),
      new Style({
        image: new CircleStyle({
          radius: 9,
          fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
        }),
      }),
    ],
  },
  // Statistique -> Marron (Brown)
  statistique: {
    default: new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "#8B4513" }), // Brown color
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
    }),
    selected: [
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: "#8B4513" }), // Brown color
          stroke: new Stroke({
            color: "white",
            width: 2,
          }),
        }),
        zIndex: 1,
      }),
      new Style({
        image: new CircleStyle({
          radius: 9,
          fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
        }),
      }),
    ],
  },
  // Autre -> Violet (Purple)
  autre: {
    default: new Style({
      image: new CircleStyle({
        radius: 5,
        fill: new Fill({ color: "purple" }),
        stroke: new Stroke({
          color: "white",
          width: 2,
        }),
      }),
    }),
    selected: [
      new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: "purple" }),
          stroke: new Stroke({
            color: "white",
            width: 2,
          }),
        }),
        zIndex: 1,
      }),
      new Style({
        image: new CircleStyle({
          radius: 9,
          fill: new Fill({ color: "rgba(0, 20, 130, 0.3)" }),
        }),
      }),
    ],
  },
};

export function createBiensOffMarketLayer(options: BiensOffMarketLayerOptions) {
  const { visible, selectedParcelle } = options;

  const source = new VectorSource({ features: [] as any[] });

  const layer = new VectorLayer({
    source,
    visible,
    properties: { name: 'biens_off_market' },
    style: (feature) => {
      const isSelected = feature.get("name") === selectedParcelle;
      const contractType = feature.get("type_de_contrat");
      const status = feature.get("status");
      
      // Determine style category based on status first, then contract type
      let styleCategory: 'vente' | 'location' | 'statistique' | 'autre' = 'autre'; // default to purple
      
      if (status === 'statistique') {
        styleCategory = 'statistique';
      } else if (contractType) {
        const contractTypeLower = contractType.toLowerCase();
        if (contractTypeLower === 'vente') {
          styleCategory = 'vente';
        } else if (contractTypeLower === 'location') {
          styleCategory = 'location';
        }
      }
      
      return isSelected 
        ? biensOffMarketStyles[styleCategory].selected 
        : biensOffMarketStyles[styleCategory].default;
    },
    zIndex: 11,
  });

  // Function to refresh data based on map extent
  const refreshData = async (mapInstance: any, currentFilters?: any) => {
    try {
      if (!visible) return;
      
      const view = mapInstance.getView();
      const extent = view.calculateExtent(mapInstance.getSize());
      const [minX, minY, maxX, maxY] = extent;
      
      // Transform extent from Web Mercator to WGS84
      const [minLon, minLat] = transform([minX, minY], 'EPSG:3857', 'EPSG:4326');
      const [maxLon, maxLat] = transform([maxX, maxY], 'EPSG:3857', 'EPSG:4326');
      
      // Build filter - use the filters as they come from the component
      let filterString = '';
      if (currentFilters && Object.keys(currentFilters).length > 0) {
        filterString = `&filter=${encodeURIComponent(JSON.stringify(currentFilters))}`;
      } else {
        filterString = '&filter[Position_sur_map][_nnull]=true&filter[status][_eq]=actif';
      }
      
      // Fetch biens_off_market data
      const response = await axiosInstance.get(
        `https://melcarnedirectus2.digipulse.ch/items/biens_off_market?fields=*${filterString}&limit=1000`
      );
      
      const biensOffMarket = response.data.data.filter((item: any) => {
        if (!item.Position_sur_map?.coordinates) return false;
        const [lon, lat] = item.Position_sur_map.coordinates;
        return lon >= minLon && lon <= maxLon && lat >= minLat && lat <= maxLat;
      });
      
      // Clear existing features
      source.clear();
      
      // Add features for biens off-market
      biensOffMarket.forEach((item: any) => {
        if (item.Position_sur_map?.coordinates) {
          const [lon, lat] = item.Position_sur_map.coordinates;
          const feature = new Feature({
            type: "geoMarker",
            geometry: new Point(fromLonLat([lon, lat])),
            name: item.NoParcelle || item.id,
            address: item.Adresse ? `${item.Adresse}, ${item.NPA} ${item.CommuneAdresse}` : '',
            dataType: "biens_off_market",
            itemId: item.id,
            // Additional details for enhanced hover
            usage: item.Usage || item.Type_de_bien,
            m2_habitable: item.m2_habitable,
            m2_terrain: item.m2_terrain,
            prix_demande: item.Prix_demande,
            date_created: item.date_created,
            commentaires: item.Commentaires || item.commentaires || item.comments,
            isOffMarket: true,
            // Contract type for styling
            type_de_contrat: item.Type_de_contrat,
            // Status for styling
            status: item.status,
          });
          source.addFeature(feature as any);
        }
      });
      
      // Show the layer after loading data
      layer.setVisible(true);
      
    } catch (error) {
      console.error('Error fetching biens off-market data:', error);
    }
  };

  // Attach refresh function to layer for external access
  (layer as any)._refreshData = refreshData;

  return layer;
} 